import { ChevronDown, Check, X } from 'lucide-react';
import { cn } from '@/utils/cn';

export interface PlayableVersion {
  id: string;
  name: string;
  previewUrl: string;
  status: 'draft' | 'published' | 'archived';
  version: number;
  description?: string;
  basePlayableId?: string;
  createdAt: string;
}

interface VersionSelectorProps {
  versions: PlayableVersion[];
  currentVersionId?: string;
  onVersionChange: (versionId: string) => void;
  onAcceptDraft?: (versionId: string) => void;
  onDiscardDraft?: (versionId: string) => void;
  className?: string;
}

export function VersionSelector({ versions, currentVersionId, onVersionChange, onAcceptDraft, onDiscardDraft, className }: VersionSelectorProps) {
  const currentVersion = versions.find(v => v.id === currentVersionId);

  // 分组：正式版 vs 草稿版
  const publishedVersions = versions.filter(v => v.status === 'published');
  const draftVersions = versions.filter(v => v.status === 'draft');

  if (versions.length === 0) return null;

  const handleAccept = (e: React.MouseEvent, versionId: string) => {
    e.stopPropagation();
    onAcceptDraft?.(versionId);
  };

  const handleDiscard = (e: React.MouseEvent, versionId: string) => {
    e.stopPropagation();
    onDiscardDraft?.(versionId);
  };

  return (
    <div className={cn('relative group', className)}>
      {/* 当前版本显示 */}
      <button className="flex items-center gap-1.5 rounded bg-muted/50 px-3 py-1.5 text-sm hover:bg-muted transition-colors">
        <span className="font-medium">
          {currentVersion?.status === 'draft' ? '📝' : '✓'}
          {currentVersion?.status === 'draft'
            ? `草稿 ${currentVersion.description?.slice(0, 20) || currentVersion.name}`
            : `V${currentVersion?.version || '?'}`}
        </span>
        <ChevronDown size={14} className="opacity-60" />
      </button>

      {/* 下拉菜单 */}
      <div className="absolute top-full right-0 mt-1 hidden group-hover:block z-50 min-w-[240px] rounded-lg border border-border bg-background shadow-lg">
        {/* 正式版本 */}
        {publishedVersions.length > 0 && (
          <div className="border-b border-border/50 p-1">
            <div className="px-2 py-1 text-xs font-semibold text-muted-foreground">正式版本</div>
            {publishedVersions.map((v) => (
              <button
                key={v.id}
                onClick={() => onVersionChange(v.id)}
                className={cn(
                  'w-full text-left px-3 py-2 rounded text-sm hover:bg-muted transition-colors',
                  v.id === currentVersionId && 'bg-primary/10 text-primary'
                )}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium">✓ V{v.version}</span>
                  <span className="text-xs text-muted-foreground">
                    {new Date(v.createdAt).toLocaleDateString('zh-CN', { month: 'short', day: 'numeric' })}
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* 草稿版本 */}
        {draftVersions.length > 0 && (
          <div className="p-1">
            <div className="px-2 py-1 text-xs font-semibold text-muted-foreground">草稿版本</div>
            {draftVersions.map((v) => (
              <div
                key={v.id}
                className={cn(
                  'rounded text-sm hover:bg-muted transition-colors',
                  v.id === currentVersionId && 'bg-primary/10'
                )}
              >
                <button
                  onClick={() => onVersionChange(v.id)}
                  className="w-full text-left px-3 py-2"
                >
                  <div className="flex flex-col gap-0.5">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">📝 {v.description?.slice(0, 30) || v.name}</span>
                      <span className="text-xs text-muted-foreground">
                        {new Date(v.createdAt).toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    {v.basePlayableId && (
                      <span className="text-xs text-muted-foreground">基于 V{v.version}</span>
                    )}
                  </div>
                </button>
                {/* Accept/Discard 按钮 */}
                <div className="flex items-center gap-1 px-3 pb-2">
                  <button
                    onClick={(e) => handleAccept(e, v.id)}
                    className="flex items-center gap-1 px-2 py-1 text-xs bg-green-500/10 text-green-600 dark:text-green-400 rounded hover:bg-green-500/20 transition-colors"
                  >
                    <Check size={12} />
                    接受
                  </button>
                  <button
                    onClick={(e) => handleDiscard(e, v.id)}
                    className="flex items-center gap-1 px-2 py-1 text-xs bg-red-500/10 text-red-600 dark:text-red-400 rounded hover:bg-red-500/20 transition-colors"
                  >
                    <X size={12} />
                    丢弃
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
